/*
 * Flip Card — interaction handler.
 *
 * Toggles data-flipped on the card root for three input modes:
 *
 *   1. Mouse — mouseover / mouseout boundary detection. Uses
 *      relatedTarget so we only fire when the cursor actually
 *      crosses the card edge (not when it moves between children).
 *      Stable through mid-animation transforms — CSS :hover would
 *      flicker because the projected hit-test area of a rotating
 *      card shrinks mid-flip.
 *
 *   2. Keyboard — focusin / focusout on any element inside the
 *      card root. The card stays flipped while focus is anywhere
 *      within it, so users can tab through back-face controls
 *      without losing the reveal.
 *
 *   3. Touch / click — click on the inner toggles. Clicks on
 *      interactive children (a / button / input / select / textarea)
 *      let the action run instead. Pressing Enter / Space on the
 *      focused inner toggles too.
 *
 * Document-level delegated listeners, no per-instance state.
 * Survives shopify:section:load re-renders for free.
 */

const SELECTOR = '[data-flip-card]';

function setFlipped(card, flipped) {
  card.dataset.flipped = flipped ? 'true' : 'false';
  const inner = card.querySelector('.flip-card__inner');
  if (inner) inner.setAttribute('aria-pressed', String(flipped));
}

// Mouse boundary detection. mouseover/out bubble naturally, but they
// fire on every descendant. Filter via relatedTarget — we only flip
// when the relatedTarget is OUTSIDE the card (i.e. the cursor
// genuinely crossed the card boundary).
document.addEventListener('mouseover', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest(SELECTOR);
  if (!card) return;
  const related = event.relatedTarget;
  if (related instanceof Node && card.contains(related)) return;
  setFlipped(card, true);
});

document.addEventListener('mouseout', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest(SELECTOR);
  if (!card) return;
  const related = event.relatedTarget;
  if (related instanceof Node && card.contains(related)) return;
  setFlipped(card, false);
});

// Keyboard focus. focusin/focusout bubble. As above, only react when
// the focus transition crosses the card boundary.
document.addEventListener('focusin', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest(SELECTOR);
  if (!card) return;
  const previous = event.relatedTarget;
  if (previous instanceof Node && card.contains(previous)) return;
  setFlipped(card, true);
});

document.addEventListener('focusout', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest(SELECTOR);
  if (!card) return;
  const next = event.relatedTarget;
  if (next instanceof Node && card.contains(next)) return;
  setFlipped(card, false);
});

// Click toggle — primarily for touch devices where hover doesn't fire.
// On desktop, mouseover already set flipped=true; the click toggle
// flips it back, but mouseleave will reset on its own anyway, so the
// state stays coherent.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const inner = event.target.closest('.flip-card__inner');
  if (!inner) return;

  // Let clicks on interactive children run their own action — only
  // toggle when the click target IS the inner itself.
  const closestInteractive = event.target.closest('a, button, input, select, textarea');
  if (closestInteractive && closestInteractive !== inner) return;

  const card = inner.closest(SELECTOR);
  if (!card) return;
  const isFlipped = card.dataset.flipped === 'true';
  setFlipped(card, !isFlipped);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (!(event.target instanceof Element)) return;
  const inner = event.target.closest('.flip-card__inner');
  if (!inner || inner !== event.target) return;

  event.preventDefault();
  const card = inner.closest(SELECTOR);
  if (!card) return;
  const isFlipped = card.dataset.flipped === 'true';
  setFlipped(card, !isFlipped);
});
